import template from './video.tpl.html';
import config from 'app/config';

/**
 * Controller for the Video component, handling Wistia and Vimeo video playback.
 *
 * @class VideoController
 */
class VideoController {

  // Ensure all dependencies are correctly injected for minification
  static $inject = ["$ocLazyLoad", "$sce", "$timeout", "userService", "$element", "$window", "$document", "PageService"];

  /** @ngInject */
  constructor($ocLazyLoad, $sce, $timeout, userService, $element, $window, $document, PageService) {
    Object.assign(this, {
      $ocLazyLoad, $sce, $timeout, userService, $element, $window, $document, PageService
    });

    // Component state initialization
    this.chkLoaded = false;
    this.videoData = null; // Renamed from 'video' to avoid conflict with binding 'video'
    this.ratio = '16:9';
    this.playlistEnabled = false;
    this.autoplayEnabled = false;
    this.videoType = '';
    this.user = {}; // Not directly used in the provided snippet, but kept for context.
    this.videoIdPrefix = `${config.appName}_video_`; // Define the prefix once
    this.componentVideoId = ''; // This will hold the full ID for this component instance
    this.page = null; // Will store the resolved page object from PageService
  }

  /**
   * Lifecycle hook: Called after the controller's bindings are initialized.
   */
  async $onInit() {
    // Resolve the page object from PageService first
    this.page = await this.PageService.getGlobalPage();

    if (!this.page) {
      console.error('VideoController: Page object not available from PageService. Cannot initialize video.');
      return; // Exit if page object isn't available
    }

    // Determine video source from component binding or resolved page object
    // Prioritize component binding, then page.header.video
    this.videoData = this.video || this.page.header?.video;

    // Use default ratio if not provided by binding or page
    this.ratio = this.ratio || '16:9';

    // Ensure boolean conversion for playlist and autoplay
    this.playlistEnabled = !!this.playlist;
    this.autoplayEnabled = localStorage.getItem('wautoplay') === 'true' || !!this.autoplay;

    // Set the unique ID for this component's video container using the determined video ID
    if (!this.videoData) {
      console.warn('VideoController: No video ID provided from binding or PageService. Cannot initialize video.');
      return; // Exit if no video ID to work with
    }
    this.componentVideoId = `${this.videoIdPrefix}${this.videoData}`;


    // Determine video type: numeric for Vimeo, otherwise Wistia (heuristic)
    this.videoType = /^\d+$/.test(this.videoData) ? 'vimeo' : 'wistia'; // Use videoData here

    // Lazy load the appropriate video library
    this.$ocLazyLoad.load(this.videoType)
      .then(() => {
        if (this.videoType === 'wistia') {
          this.initWistia();
        } else if (this.videoType === 'vimeo') {
          this.initVimeo();
        }
      })
      .catch(error => {
        console.error(`Error loading ${this.videoType} script:`, error);
        // Handle error: e.g., show a fallback message to the user
      });
  }

  /**
   * Initializes Wistia video player.
   */
  initWistia() {
    if (!this.page || !this.videoData) { // Ensure page and videoData are available
      console.error("Wistia initialization skipped: Page or video data not loaded.");
      return;
    }

    this.$window._wq = this.$window._wq || [];

    this.$window._wq.push({
      id: this.componentVideoId, // Use the stored componentVideoId
      onEmbedded: (video) => {
        this.$timeout(() => {
          this.page.wistia = video; // Use this.page
          // this.page.header.ver = video._hashedId; // Use this.page
        });

        if (this.autoplayEnabled && video.state() !== "playing") {
          video.play();
        }

        if (this.playlistEnabled) {
          this.watchPlaylist(video);
        }

        this.watchSecondChange(video);
        this.handleVisibilityChange(video);
      }
    });
  }

  /**
   * Initializes Vimeo video player.
   */
  initVimeo() {
    if (!this.$window.Vimeo || !this.$window.Vimeo.Player) {
      console.error("Vimeo Player API not loaded.");
      return;
    }

    if (!this.videoData) { // Ensure videoData is available
      console.error("Vimeo initialization skipped: No video ID available.");
      return;
    }

    // Get the video container element using the stored componentVideoId
    const vimeoContainer = this.$element[0].querySelector(`#${this.componentVideoId}`);
    if (!vimeoContainer) {
      console.error(`Vimeo container element with ID '${this.componentVideoId}' not found for video ID ${this.videoData}.`);
      return;
    }

    const videoPlayer = new this.$window.Vimeo.Player(vimeoContainer, {
      id: parseInt(this.videoData), // Use videoData here
      autoplay: this.autoplayEnabled,
    });

    videoPlayer.on('play', () => {
      // Logic on play
      // You might want to update a page property or emit an event here too
    });
    // Add other Vimeo event listeners as needed (e.g., 'ended', 'timeupdate')
  }

  /**
   * Watches for playlist events (assuming Wistia's playlist functionality).
   * @param {object} video - The Wistia video object.
   */
  watchPlaylist(video) {
    // Example: If you have custom playlist navigation, handle active class here
    // video.bind('beforeswap', (newVideo) => {
    //   this.addActiveClass(newVideo.hashedId());
    // });
    // This method depends on your specific playlist implementation.
    // If it relies on PageService, ensure this.page is used.
  }

  /**
   * Watches for video second changes to trigger content reveal.
   * @param {object} video - The Wistia video object.
   */
  watchSecondChange(video) {
    if (!this.page || !this.page.header) {
      console.warn("Wistia: Page header data not available for 'step' property.");
      return;
    }

    this.chkLoaded = true;
    const loadContentThreshold = parseFloat(this.page.header.step); // Use this.page

    if (!isNaN(loadContentThreshold)) {
      const videoContainer = this.$element[0].querySelector('.videosw');

      if (videoContainer) {
        video.bind('secondchange', (s) => {
          this.$timeout(() => {
            // Check `this.chkLoaded` state outside of the binding as well,
            // or ensure it's only set once for the duration of the video.
            if (s > loadContentThreshold && this.chkLoaded) {
              this.chkLoaded = false; // Only trigger once
              videoContainer.classList.remove('d-none');
              videoContainer.classList.add('d-block');
              // Optionally emit an event that content has been revealed
              // this.EventService.emit('contentRevealed', { videoId: this.videoData });
            }
          });
        });

        video.bind('end', () => {
          this.$timeout(() => {
            // Ensure content is shown if video ends and wasn't shown yet
            if (this.chkLoaded) { // Check if it hasn't been shown by secondchange already
              videoContainer.classList.remove('d-none');
              videoContainer.classList.add('d-block');
              this.chkLoaded = false;
            }
          });
        });
      } else {
        console.warn("Wistia: .videosw element not found within the component's template for showing content.");
      }
    } else {
      console.warn("Wistia: Invalid or missing 'step' value for content reveal. Expected a number.");
    }
  }

  /**
   * Handles page visibility changes (tab switching) to pause/play video.
   * @param {object} video - The Wistia video object.
   */
  handleVisibilityChange(video) {
    // Only apply if autoplay is enabled and visibility handling is not explicitly disabled
    if (this.autoplayEnabled && !this.visibility) { // `visibility` is a binding, check its truthiness
      let hidden, visibilityChange;

      // Determine browser-specific prefixes for Page Visibility API
      if (typeof this.$document[0].hidden !== "undefined") {
        hidden = "hidden";
        visibilityChange = "visibilitychange";
      } else if (typeof this.$document[0].msHidden !== "undefined") {
        hidden = "msHidden";
        visibilityChange = "msvisibilitychange";
      } else if (typeof this.$document[0].webkitHidden !== "undefined") {
        hidden = "webkitHidden";
        visibilityChange = "webkitvisibilitychange";
      }

      if (hidden && visibilityChange) {
        this.$window.addEventListener(visibilityChange, () => {
          this.$timeout(() => { // Use $timeout to ensure Angular's digest cycle runs
            if (this.$document[0][hidden]) {
              video.pause();
            } else {
              video.play();
            }
          });
        }, false);
      } else {
        console.warn("Page Visibility API not supported by this browser.");
      }
    }
  }

  /**
   * Adds 'active' class to the currently playing video in a playlist.
   * This method assumes a specific DOM structure for the playlist.
   * @param {string} vid - The ID of the currently playing video.
   */
  addActiveClass(vid) {
    const header = this.$element[0].querySelector("#wistia-playlist");
    if (!header) {
      console.warn("Wistia playlist header element not found within the component.");
      return;
    }

    const activeLinks = Array.from(header.getElementsByClassName("active"));
    activeLinks.forEach(link => link.classList.remove("active"));

    const newActiveLink = header.querySelector(`[href="#wistia_${vid}"]`);
    if (newActiveLink) {
      newActiveLink.classList.add("active");
    } else {
      console.warn(`Wistia playlist link for video ID ${vid} not found.`);
    }
  }

}

class VideoComponent {
  constructor() {
    this.bindings = {
      video: '@?', // Component binding for video ID
      type: '@?', // Unused in this snippet, but kept if needed for custom logic
      return: '=?', // Unused in this snippet, but kept
      autoplay: '<?', // Boolean binding for autoplay
      ratio: '@?', // Aspect ratio '16:9' or '4:3'
      playlist: '@?', // Boolean binding for playlist
      visibility: '<?' // Boolean binding to control visibility handling
    };
    this.template = template;
    this.controller = VideoController;
  }
}

export default angular
  .module(`${config.appName}video.component`, [])
  .component(`${config.appName}video`, new VideoComponent())
  .name;